import type { SeatInfo } from "@app/protocol";
import type { king } from "@app/rules";
import { SUIT_GLYPH } from "../components/Card";
import type { ToastData } from "../components/Toast";
import type { T } from "./tableText";
import { signed } from "./toastParts";

type Seats = readonly (SeatInfo | null)[];
type Event = {
  code: string;
  seat?: number | undefined;
  data?: Record<string, string | number> | undefined;
};

/** "Kız almaz", or "Koz ♠" for a trump game. */
export function kingGameText(contract: king.Contract, t: T): string {
  return contract.kind === "trump"
    ? t("king.bar.trump", { suit: SUIT_GLYPH[contract.suit] })
    : t(`king.game.${contract.penalty}`);
}

/** The same from an event's data, where the game travels as a word and a suit. */
function gameFromData(data: Event["data"], t: T): string {
  const game = String(data?.game ?? "");
  const suit = String(data?.suit ?? "");
  if (game === "trump" && suit in SUIT_GLYPH)
    return t("king.bar.trump", { suit: SUIT_GLYPH[suit as keyof typeof SUIT_GLYPH] });
  return t(`king.game.${game}` as "king.game.rifki");
}

/** A King seat's pill: naming the game, then what the hand has cost or paid so far. */
export function kingSeatSub(
  view: king.KingView,
  seat: number,
  yourSeat: number | null,
  t: T,
): string {
  if (view.phase === "choosing") {
    if (view.chooser !== seat) return t("table.seat.waiting");
    return seat === yourSeat ? t("king.seat.choose") : t("king.seat.choosing");
  }
  return t("king.seat.hand", { points: signed(view.handDeltas[seat] ?? 0) });
}

/** King events worth a sentence: the game that was named, a hand cut short. */
export function kingEventText(last: Event, seats: Seats, t: T): string | null {
  if (last.code === "contract-chosen")
    return t("king.event.chosen", {
      name: seats[last.seat ?? 0]?.name ?? "?",
      game: gameFromData(last.data, t),
    });
  if (last.code === "hand-cut-short")
    return t("king.event.cut", { game: gameFromData(last.data, t) });
  return null;
}

/** Between hands: the game, what it did to you, and every seat's line underneath. */
export function kingHandEnd(
  view: king.KingView,
  seats: Seats,
  yourSeat: number | null,
  t: T,
): ToastData | null {
  const result = view.lastHandResult;
  if (result === null) return null;
  const game = kingGameText(result.contract, t);
  const sub = result.deltas
    .map((delta, seat) => `${seats[seat]?.name ?? "?"} ${signed(delta)}`)
    .join(" · ");
  if (yourSeat === null)
    return { title: t("king.handEnd.watch", { hand: result.hand, game }), sub, tone: "neutral" };
  const mine = result.deltas[yourSeat] ?? 0;
  return {
    title: t("king.handEnd.title", { hand: result.hand, game, points: signed(mine) }),
    sub,
    tone: mine < 0 ? "error" : mine > 0 ? "success" : "neutral",
  };
}
